import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild
} from "@angular/core";
import * as _ from "lodash";
import {
  CdkDrag,
  CdkDragDrop,
  CdkDragEnter,
  CdkDropList,
  moveItemInArray,
  transferArrayItem
} from "@angular/cdk/drag-drop";
import {PlanningPanelModel} from "../../../models/planning-panel.model";
import {PlanningViewSettingsModel} from "../../../models/planning-view-settings.model";
import {PanelService} from "../../../../commons/angular/services/panel.service";
import {PlanningDragAndDropService} from "../../services/planning-drag-and-drop.service";
import {TableColumnSettings} from "../../../../commons/angular/services/table-column-manager";
import {PlanningViewSettingsRouteModel} from "../../../models/planning-view-settings.model";
import {DEFAULT_ROUTE_DRILLDOWN_COLUMN_WIDTHS} from "../../../constants/planning-column-widths.constant";
import {ROUTE_DRILLDOWN_COLUMN_GROUPS} from "../../../constants/planning-column-groups.constant";

require('./planning-draft-route-table-drilldown.scss');
@Component({
  standalone: false,
  selector: 'app-planning-draft-route-table-drilldown',
  templateUrl: './planning-draft-route-table-drilldown.html',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class PlanningDraftRouteTableDrilldownComponent implements OnInit, OnChanges, AfterViewInit, OnDestroy {

  @Input() index: any;
  @Input() panel: PlanningPanelModel;
  @Input() route: any;
  @Input() jobs: any[];
  @Input() panels: PlanningPanelModel[];
  @Input() routes: any;
  @Input() resources: any;
  @Input() settings: PlanningViewSettingsModel;
  @Input() visibleIds: string[];
  @Input() selectedJobIds: string[];
  @Input() selectedJobs: any[];
  @Input() highlightedActivity?: any;
  @Input() isLoading: boolean;
  @Input() itemElementSize: string;
  @Input() isValidateRouteVehicles: boolean;
  @Input() isAllowDraggingIntoDispatchedRoute: boolean;
  @Input() queryText: string;
  @Input() isGroupedRoutes: boolean;
  public logisticsVehicleName: string;

  @Output() onClose = new EventEmitter();
  @Output() onSelectJobs = new EventEmitter<{ jobIds: string[]; routeId: string }>();
  @Output() onViewJob = new EventEmitter<{ jobs: any[], event?: any }>();
  @Output() onVisibleRoutesChange = new EventEmitter<string[]>();
  @Output() onLockRouteChange = new EventEmitter<{ id: string, locked: boolean }>();
  @Output() onMoveActivity = new EventEmitter();
  @Output() onAddStopsToRoute = new EventEmitter();
  @Output() onDispatch = new EventEmitter<string>();
  @Output() onReoptimize = new EventEmitter<string>();
  @Output() onPrint = new EventEmitter<string>();
  @Output() onPrintLoadingSheet = new EventEmitter<string>();
  @Output() onDelete = new EventEmitter<string>();
  @Output() onPreAssignAgent = new EventEmitter<string>();
  @Output() onChangeRouteResource = new EventEmitter<{ routeId: string, event: any }>();
  @Output() onToggleObligateTimeWindow = new EventEmitter<{ jobIds: string[], obligateTimeWindow: boolean }>();
  @Output() onToggleLockJobs = new EventEmitter<{ jobIds: string[], isLocked: boolean }>();
  @Output() onEditResourceAllocation = new EventEmitter<{route: any, event: any}>();
  @Output() onToggleGroupedRoutes = new EventEmitter<void>();
  @Output() onSettingsChange = new EventEmitter<PlanningViewSettingsModel>();

  get uniqueId(): string {
    return 'route-drilldown-' + this.panel?.id;
  }
  defaultWidths: { [key: string]: number } = DEFAULT_ROUTE_DRILLDOWN_COLUMN_WIDTHS;
  columnGroups = ROUTE_DRILLDOWN_COLUMN_GROUPS;

  onRouteSettingsChange(updatedRouteSettings: any): void {
    const settingsCopy = _.cloneDeep(this.settings);
    settingsCopy.route = updatedRouteSettings;
    this.onSettingsChange.emit(settingsCopy);
  }

  @ViewChild('headerContainer', {static: false}) private headerContainer: ElementRef;
  @ViewChild('bodyContainer', {static: false}) private bodyContainer: ElementRef;

  private resizeObserver: ResizeObserver;

  public jobsMap: any;
  public activityJobsMap: any;
  public activityDetailsMap: any = {};
  public connectedPanelIds: string[];
  public selectedKeys: Object = {};
  public visibleKeys: Object;
  public routeColor: string;
  public isSmallScreen: boolean;
  public isRunning: boolean = false;

  constructor(
    @Inject(ElementRef) private elementRef,
    @Inject('planningCommonService') private planningCommonService,
    @Inject('$timeout') private $timeout,
    @Inject('planningAlertsService') private planningAlertsService,
    @Inject('planningDialogService') private planningDialogService,
    @Inject('confirmDialogService') private confirmDialogService,
    @Inject(PanelService) private panelService: PanelService,
    @Inject(ChangeDetectorRef) private cdr: ChangeDetectorRef,
    @Inject(PlanningDragAndDropService) private planningDragAndDropService: PlanningDragAndDropService) {
  }

  ngOnInit() {
    this._initActivitiesJobsMap();
    this.setRouteColor();
    this.sortPredicate = this.sortPredicate.bind(this);
    this.isSmallScreen = window.innerWidth < 1366;

    this.resizeObserver = new ResizeObserver(() => {
      this.syncHeaderScrollbarWidth();
    });
  }

  ngAfterViewInit() {
    if (this.bodyContainer) {
      this.resizeObserver.observe(this.bodyContainer.nativeElement);

      const innerWrapper = this.bodyContainer.nativeElement.querySelector('.table-inner-wrapper');
      if (innerWrapper) {
        this.resizeObserver.observe(innerWrapper);
      }
    }
    this.syncHeaderScrollbarWidth();

  }

  ngOnDestroy() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
  }

  onBodyScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (this.headerContainer) {
      this.headerContainer.nativeElement.scrollLeft = target.scrollLeft;
    }
  }

  private syncHeaderScrollbarWidth() {
    if (this.headerContainer && this.bodyContainer) {
      const bodyEl = this.bodyContainer.nativeElement;
      const headerEl = this.headerContainer.nativeElement;
      const scrollbarWidth = bodyEl.offsetWidth - bodyEl.clientWidth;
      headerEl.style.paddingRight = `${scrollbarWidth}px`;
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.selectedJobIds) {
      this.selectedKeys = {};

      this.selectedJobIds?.forEach((id) => {
        const activities = this.route.activities.filter((a) => {
          return a.jobIds ? _.includes(a.jobIds, id) : (a.jobId === id);
        })

        activities.forEach((a) => {
          this.selectedKeys[a.id] = true;
        })
      })
    }
    if (changes.route || changes.resources) {
      this.logisticsVehicleName = _.get(_.find(this.resources, {id: this.route?.resourceId}), 'mainLogisticsVehicleReference.name');
    }
    if (changes.route) {
      this._initActivitiesJobsMap();
      this.setRouteColor();
      this._setIsRouteProcessRunning();

      const currentRoute = changes.route.currentValue,
        previousRoute = changes.route.previousValue;

      if (currentRoute?.id !== previousRoute?.id) {
        if (this.bodyContainer) {
          this.bodyContainer.nativeElement.scrollTo({top: 0, behavior: 'smooth'});
        }
      }
    }

    if (changes.panels || changes.panel || changes.route || changes.routes || changes.resources) {
      const filteredPanels = _.filter(this.panels, (p) => {
        return p.selectedRouteId ? (this.route.id !== p.selectedRouteId) : true;
      })
      const containerIds = _.map(this.panels, p => p.id + '_routes_container');
      this.connectedPanelIds = _.uniq(_.concat(_.map(filteredPanels, 'id'), _.map(this.routes, 'id'), _.map(this.resources, 'id'), containerIds));
    }
    if (changes.visibleIds) {
      this.visibleKeys = {};
      _.forEach(this.visibleIds, (id) => {
        this.visibleKeys[id] = true;
      });
    }

    if (changes.queryText) {
      setTimeout(() => this.scrollToFirstHighlight(), 0);
    }

    if (changes.jobs && !changes.jobs.isFirstChange()) {
      this._initActivitiesJobsMap();
    }

    this.cdr.detectChanges();
  }

  scrollToFirstHighlight(): void {
    const el = this.elementRef.nativeElement.querySelector('.highlight');
    if (el) {
      el.scrollIntoView({behavior: 'smooth', block: 'center'});
    }
  }

  isActivityLocked(activity) {
    if (activity?.jobId) {
      return !!this.jobsMap[activity.jobId]?.lockedRouteId;
    } else if (activity?.jobIds?.length) {
      for (let id of activity.jobIds) {
        if (!!this.jobsMap[id]?.lockedRouteId) {
          return true;
        }
      }
      return false;
    }
  }

  onSelectedChange(activity: any, isSelected: boolean) {
    let selectedIds;
    let jobIds = activity.jobIds ? activity.jobIds : [activity.jobId];
    let routeId = '';

    if (isSelected) {
      selectedIds = _.uniq([...this.selectedJobIds].concat(jobIds));
      routeId = this.route.id;
    } else {
      selectedIds = this.selectedJobIds.filter(id => !jobIds.includes(id));
    }

    this.onSelectJobs.emit({ jobIds: _.compact(selectedIds), routeId: routeId });
  }

  isMultipleActivitiesDragged(jobIds) {
    return this.selectedJobIds.length > 1 && !!_.intersection(this.selectedJobIds, jobIds).length;
  }

  onToggleVisibleRoute(id, isVisible: boolean) {
    this.visibleKeys[id] = isVisible;
    const visibleIds = _.map(this.visibleKeys, (value, key) => {
      if (value) return key;
    });

    this.onVisibleRoutesChange.emit(_.compact(visibleIds));
  }

  sortPredicate(index: number, item: CdkDrag<any>, drop: CdkDropList) {

    const startIndex = _.findIndex(drop.data, (a: any) => {
      return a.type === 'START';
    })
    const endIndex = _.findIndex(drop.data, (a: any) => {
      return a.type === 'END';
    })

    if (((startIndex > -1) && (startIndex >= index)) || ((endIndex > -1) && endIndex < index)) {
      return false;
    }

    // Add Job To Route
    if (item.data.jobType) {
      return true;
    }
    // Add Activity To Route
    else {
      const stopType = item.data.stopType;

      // Drop to same route type
      const isOperationList = _.find(drop.data, (a: any) => {
        return a.stopType === 'Operation';
      })
      if (!!isOperationList && stopType !== 'Operation') {
        return false;
      }

      if (stopType === 'Operation') {
        return true;
      }


      return this.planningDragAndDropService.canReorderRouteActivity(index, item, drop, this.selectedJobIds);
    }
  }

  enter(event: CdkDragEnter) {
  }

  drop(event: CdkDragDrop<string[]>) {
    this.planningDragAndDropService.onDropRouteActivities(
      event,
      this.selectedJobIds,
      this.jobsMap,
      this.route,
      this.isValidateRouteVehicles,
      this.isAllowDraggingIntoDispatchedRoute,
      this.jobs
    ).then((payload) => {
      if (payload) {
        this.onMoveActivity.emit(payload);
      }
    });
  }

  private setRouteColor() {
    this.routeColor = this.planningCommonService.getRouteColor(this.route);
  }

  private _initActivitiesJobsMap() {
    this.jobsMap = _.keyBy(this.jobs, (j) => {
      return j.id
    })

    this.activityJobsMap = {};
    this.activityDetailsMap = {};

    this.route.activities.forEach((a) => {
      this.activityJobsMap[a.id] = [];

      if (a.jobId) {
        this.activityJobsMap[a.id].push(this.jobsMap[a.jobId]);
      }

      if (a.jobIds?.length) {
        const jobs = [];
        a.jobIds.forEach(jobId => {
          jobs.push(this.jobsMap[jobId]);
        })
        this.activityJobsMap[a.id] = this.activityJobsMap[a.id].concat(jobs);
      }

      const jobs = this.activityJobsMap[a.id] || [];

      let packages = [];
      let barcodesList = [];
      let labels = [];

      jobs.forEach((j) => {
        if (!j) return;
        let jobPackages = [];
        let jobBarcodesList = j.packages?.filter((p) => !!p.barcode).map((p) => p.barcode) || [];
        let jobLabels = [];

        j.packages?.forEach(p => {
          jobPackages = jobPackages.concat(p);

          if (p.labels?.length) {
            jobLabels = jobLabels.concat(p.labels);
          }
        })

        barcodesList = barcodesList.concat(jobBarcodesList);
        labels = labels.concat(jobLabels);
        packages = packages.concat(jobPackages);
      })

      let accountsName = [];
      let accountExternalIds = [];
      if (a?.type === 'GROUP' && a?.stopActivities?.length > 0) {
        accountsName = _.uniq(a.stopActivities?.map(sa => sa.accountName));
        accountExternalIds = _.compact(_.uniq(a.stopActivities?.map(sa => sa.accountExternalId)));
      } else {
        accountsName.push(a?.accountName || '---');
        if (a?.accountExternalId) {
          accountExternalIds.push(a.accountExternalId);
        }
      }
      const displayAccountName = (a?.type === 'GROUP' && a?.stopActivities?.length > 0)
        ? (a.stopActivities[0]?.accountName || '---')
        : (a?.accountName || '---');

      let stopRef = null;
      if (jobs.length) {
        const firstJob = jobs[0];
        if (firstJob) {
          stopRef = _.find(firstJob.stops, (s: any) => {
            return !!a.stopIds?.length ? _.includes(a.stopIds, s.id) : s.id === a.stopId;
          })
        }
        if (!stopRef) {
          const allStops = _.flatMap(jobs, 'stops');
          stopRef = _.find(allStops, (s: any) => {
            return !!a.stopIds?.length ? _.includes(a.stopIds, s.id) : s.id === a.stopId;
          });
        }
      }

      this.activityDetailsMap[a.id] = {
        jobs: jobs,
        packages: _.uniqBy(packages, 'size'),
        barcodesList: _.uniq(barcodesList),
        labels: _.uniq(labels),
        accountsName: accountsName,
        accountExternalIds: accountExternalIds,
        displayAccountName: displayAccountName,
        stopRef: stopRef,
        firstJob: jobs[0] || null,
        isLocked: _.some(jobs, (j) => !!j?.lockedRouteId)
      };
    });
  }

  private _setIsRouteProcessRunning() {
    this.isRunning = this.route.actionProcess?.running && (new Date().getTime() < this.route.actionProcess?.expirationDate);
  }

  trackById(_: number, item: { id: string | number }) {
    return item.id;
  }
}
